import React from "react";
import { LuWandSparkles } from "react-icons/lu";
import CustomButton from "../../../commonComponents/button";
import {
  AI_CHANNEL,
  REFINE_DISABLED_TOOLTIP_BY_CHANNEL,
  REFINE_TOOLTIP_BY_CHANNEL,
} from "./constants";

/**
 * One-click "Refine message" (EAI-1006).
 *
 * Opens nothing — it reworks whatever is already in the composer. Drop it into
 * any composer's control group and hand it the `refine`/`refining` pair from
 * `useRefinableDraft` (or from `useAiDraftWriter` directly). Icon-only by
 * default; pass `label` for surfaces that show it as a standalone button.
 *
 * With nothing to refine it greys out but stays clickable, and the click is
 * dropped instead. antd's own `disabled` would stop the button firing mouse
 * events, which takes the wrapping Tooltip with it — and that tooltip is what
 * tells the user why the control is unavailable. `aria-disabled` carries the
 * state for assistive tech in its place.
 *
 * @param {{
 *   channel?: string,        // an AI_CHANNEL; picks the wording of the tooltips
 *   onRefine: Function,
 *   refining?: boolean,      // a refine is in flight — shows a spinner
 *   hasDraft?: boolean,      // false greys it out; there is nothing to refine
 *   className?: string,
 *   label?: string,          // omit for the icon-only form used in a button group
 * }} props
 */
export default function RefineButton({
  channel = AI_CHANNEL.TEXT,
  onRefine,
  refining = false,
  hasDraft = false,
  className = "",
  label = null,
}) {
  const unavailable = !hasDraft || refining;
  // An unknown channel falls back to the text wording rather than rendering an
  // empty tooltip.
  const tooltip = hasDraft
    ? REFINE_TOOLTIP_BY_CHANNEL[channel] || REFINE_TOOLTIP_BY_CHANNEL[AI_CHANNEL.TEXT]
    : REFINE_DISABLED_TOOLTIP_BY_CHANNEL[channel] ||
      REFINE_DISABLED_TOOLTIP_BY_CHANNEL[AI_CHANNEL.TEXT];

  return (
    <CustomButton
      type="text"
      // Never a submit: this sits inside the composer's <Form>, and CustomButton
      // leaves htmlType as "" — an invalid value, which HTML resolves to "submit".
      // Without this, refining also sends the message.
      htmlType="button"
      // A labelled button sizes to its content; the icon-only form is a 30px
      // square so it lines up with the other controls in a composer's button
      // group. Pass `className` to size it differently.
      className={`flex items-center justify-center ${label ? "" : "p-0 w-[30px] h-[30px]"} ${
        unavailable ? "cursor-not-allowed opacity-40" : ""
      } ${className}`}
      icon={<LuWandSparkles className="text-[#334155] text-[18px]" />}
      loading={refining}
      btnText={label}
      tooltipTitle={tooltip}
      ariaDisabled={unavailable}
      handleClick={() => {
        if (unavailable) return;
        onRefine();
      }}
    />
  );
}
